import React, { useEffect, useState } from "react";
import {
  IconButton,
  TextField,
  Button,
  CircularProgress,
  MenuItem,
  Grid,
  Autocomplete,
  Typography,
  Drawer,
  Toolbar,
  Paper,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import axios from "axios";
import {
  AGENT_DATA_EXPORT,
  CLOSED_SUB_CATEGORY,
  COLD_SUB_CATEGORY,
  EXCEL_EXPORT_ALL_DATA_SUPERVISOR,
  GET_BDM_LIST,
  GET_CAMPAIGN_NAME,
  GET_CATEGORY,
  GET_CSE_LIST,
  GET_INQUIRY_TYPE,
  GET_PROJECT,
  GET_REGION_LIST,
  HOT_SUB_CATEGORY,
  PENDING_SUB_CATEGORY,
  SUPERVISOR_DATA_EXPORT,
  WARM_SUB_CATEGORY,
} from "../../constants";
import FilterListIcon from "@mui/icons-material/FilterList"; // Add this import for the filter icon
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const FilterPanel = ({ filters, setFilters, onApplyFilters }) => {
  const [inquiryTypes, setInquiryTypes] = useState([]);
  const [regionList, setRegionList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [campaignList, setCampaignList] = useState([]);
  const [bdmList, setBdmList] = useState([]);
  const [cseList, setCseList] = useState([]);
  const [projectList, setProjectList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [regionLoading, setRegionLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [projectLoading, setProjectLoading] = useState(false);
  const [bdmLoading, setBdmLoading] = useState(false);
  const [cseLoading, setCseLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false); // State to control drawer visibility

  useEffect(() => {
    const savedFilters = localStorage.getItem("filters");
    const parsedFilters = savedFilters ? JSON.parse(savedFilters) : filters;
    parsedFilters.page = Number(parsedFilters.page || 0); // Ensure page is a number
    parsedFilters.pageSize = Number(parsedFilters.pageSize || 20); // Ensure pageSize is a number
    setFilters(parsedFilters);
    onApplyFilters(parsedFilters);

    // Merge pre-selected values into retained filters
    const populateRetainedOptions = () => {
      const inquiryTypeOptions =
        parsedFilters.InquiryType?.split(",").map((value) => ({
          InquiryType: value,
        })) || [];
      const categoryOptions =
        parsedFilters.category?.split(",").map((value) => ({
          category: value,
        })) || [];
      const regionOptions =
        parsedFilters.region?.split(",").map((value) => ({
          region_name: value,
        })) || [];
      const projectOptions =
        parsedFilters.Project?.split(",").map((value) => ({
          Project: value,
        })) || [];
      const campaignOptions =
        parsedFilters.campaignName?.split(",").map((value) => ({
          CampaignName: value,
        })) || [];
      const leadOwnerOptions =
        parsedFilters.leadowner?.split(",").map((value) => ({
          EmployeeId: value,
          EmployeeName: value, // Temporary fallback for display
        })) || [];

      setInquiryTypes([...inquiryTypeOptions]); // Add retained options to state
      setCategoryList([...categoryOptions]);
      setRegionList([...regionOptions]); // Add retained options to state
      setProjectList([...projectOptions]); // Add retained options to state
      setCampaignList([...campaignOptions]); // Add retained options to state
      setBdmList([...leadOwnerOptions]); // Add retained options to state
    };

    populateRetainedOptions();
  }, [setFilters]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("filters", JSON.stringify(filters));
  }, [filters]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const fetchInquiryTypes = async () => {
    // Only fetch if options are not already loaded
    setLoading(true);
    try {
      const response = await axios.get(GET_INQUIRY_TYPE);
      setInquiryTypes(response.data);
    } catch (error) {
      console.error("Error fetching inquiry types:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutocompleteChange = (event, newValue) => {
    const newInquiryTypes = newValue.map((item) => item.InquiryType).join(","); // Join the selected values with commas
    setFilters((prevFilters) => ({
      ...prevFilters,
      InquiryType: newInquiryTypes, // Save as comma-separated string
    }));
  };

  const fetchRegionList = async () => {
    // Only fetch if options are not already loaded
    setRegionLoading(true);
    try {
      const response = await axios.get(GET_REGION_LIST);
      setRegionList(response.data);
    } catch (error) {
      console.error("Error fetching inquiry types:", error);
    } finally {
      setRegionLoading(false);
    }
  };

  const handleRegionAutocompleteChange = (event, newValue) => {
    // Create a comma-separated string from selected region names
    const regionNames = newValue.map((item) => item.region_name).join(",");

    setFilters((prevFilters) => ({
      ...prevFilters,
      region: regionNames, // Save the comma-separated region names
    }));
  };

  const fetchCategoryList = async () => {
    // Only fetch if options are not already loaded
    setCategoryLoading(true);
    try {
      const response = await axios.get(GET_CATEGORY);
      setCategoryList(response.data);
    } catch (error) {
      console.error("Error fetching inquiry types:", error);
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleCategoryAutocompleteChange = (event, newValue) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      category: newValue ? newValue.category : "",
    }));
  };

  const fetchProjectList = async () => {
    // Only fetch if options are not already loaded
    setProjectLoading(true);
    try {
      const response = await axios.get(GET_PROJECT);
      setProjectList(response.data);
    } catch (error) {
      console.error("Error fetching inquiry types:", error);
    } finally {
      setProjectLoading(false);
    }
  };

  const handleProjectAutocompleteChange = (event, newValue) => {
    // Create a comma-separated string from selected project names
    const projectNames = newValue.map((item) => item.Project).join(",");

    setFilters((prevFilters) => ({
      ...prevFilters,
      Project: projectNames, // Save the comma-separated project names
    }));
  };

  const fetchCampaignList = async () => {
    // Only fetch if options are not already loaded
    setCampaignLoading(true);
    try {
      const response = await axios.get(GET_CAMPAIGN_NAME);
      setCampaignList(response.data);
    } catch (error) {
      console.error("Error fetching inquiry types:", error);
    } finally {
      setCampaignLoading(false);
    }
  };

  const handleCampaignAutocompleteChange = (event, newValue) => {
    const selectedCampaigns = newValue
      .map((item) => item.CampaignName)
      .join(","); // Create comma-separated list
    setFilters((prevFilters) => ({
      ...prevFilters,
      campaignName: selectedCampaigns, // Store as comma-separated string
    }));
  };

  const fetchBdmList = async () => {
    // Only fetch if options are not already loaded
    setBdmLoading(true);
    try {
      const response = await axios.get(GET_BDM_LIST);
      setBdmList(response.data);
    } catch (error) {
      console.error("Error fetching inquiry types:", error);
    } finally {
      setBdmLoading(false);
    }
  };

  const handleLeadOwnerAutocompleteChange = (event, newValue) => {
    // Store only EmployeeIds as comma-separated string
    const newLeadOwnerValues = newValue
      .map((item) => item.EmployeeId)
      .join(",");
    setFilters((prevFilters) => ({
      ...prevFilters,
      leadowner: newLeadOwnerValues, // Save EmployeeIds as comma-separated string
    }));
  };

  const fetchCseList = async () => {
    // Only fetch if options are not already loaded
    setCseLoading(true);
    try {
      const response = await axios.get(GET_CSE_LIST);
      setCseList(response.data);
    } catch (error) {
      console.error("Error fetching inquiry types:", error);
    } finally {
      setCseLoading(false);
    }
  };

  const handleCseAutocompleteChange = (event, newValue) => {
    const newCseValues = newValue.map((item) => item.EmployeeId).join(",");
    setFilters((prevFilters) => ({
      ...prevFilters,
      agentName: newCseValues, // Save EmployeeIds as comma-separated string
    }));
  };

  const ApplyClearAll = () => {
    const clearedFilters = {
      InquiryType: "",
      Project: "",
      region: "",
      category: "",
      subcategory: "",
      campaignName: "",
      leadowner: "",
      agentName: '',
      search: "",
      page: 0, // Reset to the first page
      pageSize: 20, // Default page size
      last_action_date: "",
      followUpDate: ""
    };

    setFilters(clearedFilters);
    onApplyFilters(clearedFilters);
    localStorage.removeItem("filters");
    setDrawerOpen(false); // Close the drawer after clearing filters
  };

  // Function to check if any filter is active
  const isFilterActive = () => {
    // Exclude page and pageSize keys
    const filtersToCheck = { ...filters };
    delete filtersToCheck.page;
    delete filtersToCheck.pageSize;

    // Check if any filter value (other than page and pageSize) is active
    return Object.values(filtersToCheck).some((value) => value !== "");
  };

  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleCloseModal = () => {
    setIsDownloading(false);
  };

  const handleExport = () => {
    setIsDownloading(true); // Open progress modal
    setDownloadProgress(0); // Reset progress

    let artificialProgressInterval;

    // Start artificial progress if the real progress is too fast
    artificialProgressInterval = setInterval(() => {
      setDownloadProgress((prevProgress) => {
        // Stop artificial progress at 90%
        if (prevProgress >= 70) {
          clearInterval(artificialProgressInterval);
          return prevProgress;
        }
        return prevProgress + 10; // Increment by 10 every 100ms
      });
    }, 100); // Update every 100ms

    axios
      .get(
        `${SUPERVISOR_DATA_EXPORT}?InquiryType=${filters.InquiryType || ""}&Project=${
          filters.Project || ""
        }&region=${filters.region || ""}&category=${filters.category || ""}&subcategory=${
          filters.subcategory || ""
        }&campaignName=${filters.campaignName || ""}&BdmID=${
          filters.leadowner || ""
        }&agentName=${filters.agentName || ""}&search=${
          filters.search === undefined ? "" : filters.search
        }&updatedDate=${filters.last_action_date === null ? "" : filters.last_action_date}&followUpDate=${filters.followUpDate === null ? "" : filters.followUpDate}`,
        {
          responseType: "blob",

          timeout: 60000, // 1-minute timeout
          onDownloadProgress: (progressEvent) => {
            if (progressEvent.lengthComputable) {
              const realProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );

              // Stop artificial progress when real progress updates
              clearInterval(artificialProgressInterval);

              // Update progress based on real data, but ensure it doesn't go backwards
              setDownloadProgress((prevProgress) =>
                Math.max(prevProgress, realProgress)
              );
            } else {
              console.warn("Unable to calculate download progress");
            }
          },
        }
      )
      .then((response) => {
        clearInterval(artificialProgressInterval); // Ensure artificial progress is cleared

        // Create file download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let fileName = "LeadExport.xlsx";
        if (contentDisposition) {
          const matches = contentDisposition.match(/filename="(.+)"/);
          if (matches && matches.length > 1) {
            fileName = matches[1];
          }
        }
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Close modal and show success
        setIsDownloading(false);
        setDownloadProgress(0);
        // setSeverity('success');
        // setMessage('File downloaded successfully');
        // setOpenSnackbar(true);
      })
      .catch((error) => {
        clearInterval(artificialProgressInterval);
        console.error("Error downloading file:", error);
        setIsDownloading(false);
        setDownloadProgress(0);
        // setSeverity('error');
        // setMessage('Failed to download the file');
        // setOpenSnackbar(true);
      });
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;

    setFilters((prevFilters) => {
      const newFilters = {
        ...prevFilters,
        search: value === undefined ? "" : value,
      };
      // Apply the filter immediately when search text is updated
      onApplyFilters(newFilters);

      return newFilters;
    });
  };

  return (
    <div>
      <Dialog
        open={isDownloading}
        onClose={(event, reason) => {
          // Disable closing when clicking outside the modal or pressing 'Escape'
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseModal();
          }
        }}
      >
        <DialogTitle>File Download in Progress...</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "250px",
              minHeight: "150px",
            }}
          >
            {/* Circular Progress with larger size */}
            <CircularProgress
              variant="determinate"
              value={downloadProgress}
              size={80}
              thickness={4}
              sx={{ color: downloadProgress < 100 ? "#1976d2" : "#4caf50" }} // Progress color change
            />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: "bold" }}>
              {downloadProgress}%
            </Typography>
            {/* Optional text for completed state */}
            {downloadProgress === 100 && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Download complete! Preparing your file...
              </Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
        }}
      >
        {/* Search Box */}
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search..."
          value={filters.search}
          onChange={handleSearchChange} // Trigger search on change
          sx={{ width: "300px" }}
        />

        {/* Filter Toggle Button with Badge */}
        <Badge
          color="secondary"
          variant="dot"
          invisible={!isFilterActive()}
          sx={{
            "& .MuiBadge-dot": {
              width: "10px", // Adjust badge width
              height: "10px", // Adjust badge height
              borderRadius: "50%", // Make it circular
              right: 0,
              top: "10px",
            },
          }}
        >
          <IconButton
            onClick={() => setDrawerOpen((prev) => !prev)}
            style={{
              border: "1px solid #fff", // Change border color to white for contrast
              borderRadius: "4px",
              margin: "5px",
              width: "40px", // Increased width for a more comfortable click area
              height: "40px", // Increased height for a more comfortable click area
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#097969",
              color: "#fff",
              transition: "background-color 0.3s, transform 0.2s", // Smooth transition effects
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", // Add shadow for depth
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#0B9B73"; // Darker shade on hover
              e.currentTarget.style.transform = "scale(1.05)"; // Slightly scale up on hover
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#097969"; // Original color
              e.currentTarget.style.transform = "scale(1)"; // Reset scale
            }}
          >
            {drawerOpen ? <FilterListOffIcon /> : <FilterListIcon />}{" "}
            {/* Change icon based on state */}
          </IconButton>
        </Badge>

        {/* Export Button */}
        <IconButton
          onClick={() => handleExport()}
          style={{
            border: "1px solid #fff", // Change border color to white for contrast
            borderRadius: "4px",
            margin: "5px",
            width: "40px", // Increased width for a more comfortable click area
            height: "40px", // Increased height for a more comfortable click area
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#097969",
            color: "#fff",
            transition: "background-color 0.3s, transform 0.2s", // Smooth transition effects
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", // Add shadow for depth
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#0B9B73"; // Darker shade on hover
            e.currentTarget.style.transform = "scale(1.05)"; // Slightly scale up on hover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#097969"; // Original color
            e.currentTarget.style.transform = "scale(1)"; // Reset scale
          }}
        >
          <FileDownloadIcon /> {/* Change icon based on state */}
        </IconButton>
      </div>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)} // Close drawer on backdrop click
        sx={{
          width: 320,
          // zIndex: 1301,  // Higher zIndex than AppBar (default is 1100)
          overflowY: "auto",
          "& .MuiDrawer-paper": {
            width: 320, // Ensure Drawer paper width is fixed
            overflow: "hidden",
          },
        }} // Set width of the drawer
      >
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Filter Options
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setDrawerOpen(false)}
          >
            <ClearIcon />
          </IconButton>
        </Toolbar>

        <div
          style={{
            padding: 16,
            overflowY: "auto",
            maxHeight: "calc(100vh - 64px)",
          }}
        >
          {" "}
          {/* Added overflow and maxHeight */}
          <Grid
            container
            spacing={1}
            style={{ marginBottom: 16 }}
            direction="column"
          >
            <Grid item xs={12}>
              {/* Inquiry Type Filter */}
              <Autocomplete
                multiple
                options={inquiryTypes}
                getOptionLabel={(option) => option.InquiryType || ""}
                value={
                  filters.InquiryType
                    ? inquiryTypes.filter((type) =>
                        filters.InquiryType.split(",").includes(
                          type.InquiryType
                        )
                      )
                    : []
                } // Convert comma-separated string to array for value
                onChange={handleAutocompleteChange}
                onOpen={fetchInquiryTypes}
                loading={loading}
                PaperComponent={(props) => (
                  <Paper {...props} style={{ zIndex: 1400 }} />
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Inquiry Type"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    width: "100%", // Ensure the width is 100% for the field
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Region Filter */}
              <Autocomplete
                multiple
                options={regionList}
                getOptionLabel={(option) => option.region_name || ""}
                value={
                  filters.region
                    ? regionList.filter((type) =>
                        filters.region.split(",").includes(type.region_name)
                      ) || []
                    : []
                }
                onChange={handleRegionAutocompleteChange}
                onOpen={fetchRegionList}
                loading={regionLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {regionLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    width: "100%", // Ensure the width is 100% for the field
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Category Filter */}
              <Autocomplete
                options={categoryList}
                getOptionLabel={(option) =>
                  option.category
                    ? option.category.charAt(0).toUpperCase() +
                      option.category.slice(1)
                    : ""
                }
                value={
                  filters.category
                    ? categoryList.find(
                        (type) => type.category === filters.category
                      ) || null
                    : null
                }
                onChange={handleCategoryAutocompleteChange}
                onOpen={fetchCategoryList} // Trigger API call on open
                loading={categoryLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {categoryLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    width: "100%", // Ensure the width is 100% for the field
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Subcategory Filter */}
              <TextField
                name="subcategory"
                select
                label="Sub-Category"
                value={filters.subcategory}
                onChange={handleInputChange}
                fullWidth
                size="small"
                sx={{
                  "& .MuiFormHelperText-root": {
                    fontSize: "0.65rem",
                    marginLeft: "0",
                  },
                }}
              >
                {filters.category.toLowerCase() == "hot" &&
                  HOT_SUB_CATEGORY.map((h) => (
                    <MenuItem
                      value={h.label}
                      sx={{
                        borderBottom: "thin solid rgba(0, 0, 0, 0.12)",
                        marginBottom: "8px",
                        marginTop: "8px",
                      }}
                    >
                      {h.label}
                    </MenuItem>
                  ))}
                {filters.category.toLowerCase() == "warm" &&
                  WARM_SUB_CATEGORY.map((h) => (
                    <MenuItem
                      value={h.label}
                      sx={{
                        borderBottom: "thin solid rgba(0, 0, 0, 0.12)",
                        marginBottom: "8px",
                        marginTop: "8px",
                      }}
                    >
                      {h.label}
                    </MenuItem>
                  ))}
                {filters.category.toLowerCase() == "cold" &&
                  COLD_SUB_CATEGORY.map((h) => (
                    <MenuItem
                      value={h.label}
                      sx={{
                        borderBottom: "thin solid rgba(0, 0, 0, 0.12)",
                        marginBottom: "8px",
                        marginTop: "8px",
                      }}
                    >
                      {h.label}
                    </MenuItem>
                  ))}
                {filters.category.toLowerCase() == "pending" &&
                  PENDING_SUB_CATEGORY.map((h) => (
                    <MenuItem
                      value={h.label}
                      sx={{
                        borderBottom: "thin solid rgba(0, 0, 0, 0.12)",
                        marginBottom: "8px",
                        marginTop: "8px",
                      }}
                    >
                      {h.label}
                    </MenuItem>
                  ))}
                {filters.category.toLowerCase() == "closed" &&
                  CLOSED_SUB_CATEGORY.map((h) => (
                    <MenuItem
                      value={h.label}
                      sx={{
                        borderBottom: "thin solid rgba(0, 0, 0, 0.12)",
                        marginBottom: "8px",
                        marginTop: "8px",
                      }}
                    >
                      {h.label}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              {/* Project Filter */}
              <Autocomplete
                multiple
                options={projectList}
                getOptionLabel={(option) => option.Project || ""}
                value={
                  filters.Project
                    ? projectList.filter((type) =>
                        filters.Project.split(",").includes(type.Project)
                      ) || []
                    : []
                }
                onChange={handleProjectAutocompleteChange}
                onOpen={fetchProjectList}
                loading={projectLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Project"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {projectLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    width: "100%", // Ensure the width is 100% for the field
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Lead Source Filter */}
              <Autocomplete
                multiple
                options={campaignList}
                getOptionLabel={(option) => option.CampaignName || ""}
                value={
                  filters.campaignName
                    ? campaignList.filter((type) =>
                        filters.campaignName
                          .split(",")
                          .includes(type.CampaignName)
                      )
                    : []
                }
                onChange={handleCampaignAutocompleteChange}
                loading={campaignLoading}
                onOpen={fetchCampaignList}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lead Source"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {campaignLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              {/* Lead Owner Filter */}
              <Autocomplete
                multiple
                options={bdmList}
                getOptionLabel={(option) => option.EmployeeName || ""}
                value={
                  filters.leadowner
                    ? bdmList.filter((type) =>
                        filters.leadowner
                          .split(",")
                          .includes(type.EmployeeId.toString())
                      )
                    : [] // Convert comma-separated string to array for value
                }
                onChange={handleLeadOwnerAutocompleteChange}
                onOpen={fetchBdmList}
                loading={bdmLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Lead Owner"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {bdmLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    width: "100%", // Ensure the width is 100% for the field
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              {/* CSE Filter */}
              <Autocomplete
                multiple
                options={cseList}
                getOptionLabel={(option) => option.EmployeeName || ""}
                value={
                  filters.agentName
                    ? cseList.filter((type) =>
                        filters.agentName
                          .split(",")
                          .includes(type.EmployeeId.toString())
                      )
                    : [] // Make sure this is always an array
                }
                onChange={handleCseAutocompleteChange}
                onOpen={fetchCseList}
                loading={cseLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="CSE"
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {cseLoading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-inputRoot": {
                    width: "100%", // Ensure the width is 100% for the field
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Last Action Date"
                type="date"
                fullWidth
                size="small"
                name="last_action_date"
                InputLabelProps={{ shrink: true }}
                value={filters.last_action_date ? filters.last_action_date : ""}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Follow-up Date"
                type="date"
                fullWidth
                size="small"
                name="followUpDate"
                InputLabelProps={{ shrink: true }}
                value={filters.followUpDate ? filters.followUpDate : ""}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Apply and Clear Buttons */}
            <Grid item xs={12} style={{ textAlign: "right" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  onApplyFilters(filters);
                  setDrawerOpen(false);
                }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={ApplyClearAll}
                style={{ marginLeft: 8 }}
              >
                Clear All
              </Button>
            </Grid>
          </Grid>
        </div>
      </Drawer>
    </div>
  );
};

export default FilterPanel;
